/**
 * @file `AdminShell` — root component the router renders for every
 *       admin route.
 * @module @labcraft/admin/ui/kernel/admin-shell
 *
 * Boot flow on mount:
 *
 *   1. `<ClientProvider>` (one level up in `main.tsx`) has already
 *      configured + started the `Admin` Client by the time this
 *      component renders.
 *   2. `useMe()` fires its first fetch and resolves the current
 *      `MeResponse`. Anonymous admins get the "Sign in" button +
 *      `SignInPopover` (Google Workspace only); signed-in admins get
 *      the `AccountMenu`. Same widget shape as www and lab.
 *   3. Mid-cluster nav items (`Learners`, `Settings`) render ONLY
 *      when the operator is signed in — there's nothing for an
 *      anonymous visitor to click before SSO completes.
 *   4. The `<Outlet />` body renders behind the chrome; anonymous
 *      visitors on a non-home route get bounced to `/`. Routed
 *      panels also gate on `admin !== null` themselves (today the
 *      only authed panel is Learners, which 401s on a server
 *      roundtrip when anonymous) — defense in depth on top of the
 *      nav-hide + the redirect.
 *   5. Theme lives on `documentElement` and is owned by the shared
 *      `ThemeToggle` (cookie at `.labcraft.dev`) — the shell no
 *      longer participates. Density (operator-tool only) stays in
 *      `ShellContext` for future tweaks-panel wiring.
 *
 * Phase 9 of umbrella #406: replaced the `useAdmin()` external-store
 * subscription with `useMe()` (TanStack-backed). The chrome's
 * "signed in?" branch now bounces synchronously via the 401
 * callback's `setQueryData(qkMe, { admin: null })`.
 */

import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type JSX,
} from 'react'
import { Outlet, useLocation, useNavigate } from 'react-router'
import { CoreError } from '@labcraft/core'
import { AdminTopNav, type AdminNavItem } from './admin_top_nav.tsx'
import { useMe, useSignoutMutation } from '../hooks/index.ts'
import { CmdkPalette, CmdkTrigger } from './cmdk_palette.tsx'
import {
  ShellContext,
  type Density,
  type ShellContextValue,
} from './shell_context.ts'

/**
 * Panels rendered in {@link AdminTopNav}'s middle cluster, in the redesign's
 * target order (umbrella #883). The `lc>` logo + Dashboard both land on
 * `/` (the default operator home). `Emails` (#1598) is the mass-mail
 * sub-app; its nav item covers every `/emails/*` route (the compose,
 * campaigns, and suppressions surfaces share one tab).
 */
const PANELS: ReadonlyArray<{ href: string; label: string }> = [
  { href: '/', label: 'Dashboard' },
  { href: '/courses', label: 'Courses' },
  { href: '/recalibration', label: 'Recalibration' },
  { href: '/hypervisors', label: 'Hypervisors' },
  { href: '/learners', label: 'Learners' },
  { href: '/emails', label: 'Emails' },
  { href: '/settings', label: 'Settings' },
]

/**
 * Whether a nav href is the active panel for the current path. The
 * Dashboard href (`/`) matches exactly so it isn't active on every
 * route (every path `startsWith('/')`); deeper panels match by prefix
 * so a row-detail subpath keeps the tab lit.
 *
 * @param href - The nav item's href.
 * @param pathname - The current location pathname.
 * @returns Whether the item should render as the active tab.
 */
function isActiveNav(href: string, pathname: string): boolean {
  return href === '/' ? pathname === '/' : pathname.startsWith(href)
}

/** Endpoint that starts the Workspace SSO flow on the admin daemon. */
const ADMIN_SIGNIN_BEGIN = '/api/account/google-workspace/authorize'

/** Toast auto-dismiss window in milliseconds. */
const TOAST_TTL_MS = 1700

/** Read the current data-attribute on `<html>`; falls back to default. */
function readRootAttr<T extends string>(name: string, fallback: T): T {
  const v = document.documentElement.getAttribute(name) as T | null
  return v ?? fallback
}

/** Top-level admin shell — kernel chrome + routed body. */
export function AdminShell(): JSX.Element {
  const location = useLocation()
  const navigate = useNavigate()
  const me = useMe()
  const signout = useSignoutMutation()
  const admin = me.data?.admin ?? null
  const [toast, setToast] = useState<string | null>(null)
  const [density, setDensityState] = useState<Density>(() =>
    readRootAttr<Density>('data-density', 'compact'),
  )
  const [cmdkOpen, setCmdkOpenState] = useState(false)
  // When a panel registers a ⌘K handler, the shell defers to it instead
  // of the generic palette. No panel sets one today; the ref keeps the
  // design's per-panel-override contract live.
  const cmdkHandlerRef = useRef<(() => void) | null>(null)

  // Anonymous on a non-home route → bounce to `/` (anonymous home).
  // Covers both signout (admin flips to null while the operator is on
  // /learners and the panel would otherwise keep its cached rows
  // visible) and direct anonymous visits to a deep link.
  //
  // Gated on `me.data` so the pre-resolve render (where `admin` is
  // null because the query hasn't returned yet) doesn't fire a
  // spurious redirect.
  useEffect(() => {
    if (me.data && admin === null && location.pathname !== '/') {
      navigate('/', { replace: true })
    }
  }, [me.data, admin, location.pathname, navigate])

  // Push density onto the document so all panel CSS reacts.
  useEffect(() => {
    document.documentElement.setAttribute('data-density', density)
    try {
      localStorage.setItem('lc-admin-density', density)
    } catch {
      /* ignore */
    }
  }, [density])

  // Toast dismissal timer.
  useEffect(() => {
    if (toast === null) return undefined
    const id = window.setTimeout(() => {
      setToast(null)
    }, TOAST_TTL_MS)
    return () => {
      window.clearTimeout(id)
    }
  }, [toast])

  // Global chrome hotkeys: ⌘K toggles the palette (or defers to a
  // panel-registered handler); `/` focuses a panel's search box when one
  // is present and the operator isn't already typing. Esc-to-close is
  // owned by the palette itself.
  useEffect(() => {
    function onKey(e: KeyboardEvent): void {
      const tag = document.activeElement?.tagName
      const inField = tag === 'INPUT' || tag === 'TEXTAREA'
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault()
        if (cmdkHandlerRef.current) cmdkHandlerRef.current()
        else setCmdkOpenState(v => !v)
        return
      }
      if (!inField && e.key === '/') {
        const search = document.querySelector<HTMLElement>(
          '[data-admin-search]',
        )
        if (search) {
          e.preventDefault()
          search.focus()
        }
      }
    }
    window.addEventListener('keydown', onKey)
    return () => {
      window.removeEventListener('keydown', onKey)
    }
  }, [])

  const fireToast = useCallback((msg: string) => {
    setToast(msg)
  }, [])
  const setDensity = useCallback((d: Density) => {
    setDensityState(d)
  }, [])
  const setCmdkOpen = useCallback((open: boolean) => {
    setCmdkOpenState(open)
  }, [])
  const registerCmdK = useCallback((handler: (() => void) | null) => {
    cmdkHandlerRef.current = handler
  }, [])

  const ctx = useMemo<ShellContextValue>(
    () => ({
      toast: fireToast,
      density,
      setDensity,
      cmdkOpen,
      setCmdkOpen,
      registerCmdK,
    }),
    [fireToast, density, setDensity, cmdkOpen, setCmdkOpen, registerCmdK],
  )

  const onSignOut = useCallback(() => {
    signout.mutate(undefined, {
      onError: err => {
        // Mirror the pre-Phase-9 behaviour — warn, don't surface to
        // the operator; the chrome already bounces back to anonymous
        // via the 401 callback the next time any authed call fires,
        // and a transient signout failure is rare enough to swallow.
        // eslint-disable-next-line no-console
        console.warn('signout failed', CoreError.from(err).toJSON())
      },
    })
  }, [signout])

  // Palette action dispatch, keyed by the command id from `cmdk_view`.
  // Only admin-owned verbs: density is a kernel concern, sign-out is
  // shell chrome. (Theme is deliberately absent — it moved to the shared
  // cookie-backed toggle.)
  const onCmdkAction = useCallback(
    (id: string) => {
      if (id === 'toggle-density') {
        setDensityState(d => (d === 'compact' ? 'cozy' : 'compact'))
      } else if (id === 'sign-out') {
        onSignOut()
      }
    },
    [onSignOut],
  )

  // Only render mid-cluster nav items when signed in. Anonymous
  // visitors see the title + Sign In button; everything else is
  // gated until SSO completes.
  const navItems: AdminNavItem[] = admin
    ? PANELS.map(p => ({
        label: p.label,
        href: p.href,
        ...(isActiveNav(p.href, location.pathname) && { active: true }),
      }))
    : []

  return (
    <ShellContext.Provider value={ctx}>
      <div className="shell">
        <AdminTopNav
          items={navItems}
          trailing={
            admin ? (
              <CmdkTrigger
                onOpen={() => {
                  setCmdkOpenState(true)
                }}
              />
            ) : undefined
          }
          authUser={
            admin
              ? {
                  displayName: admin.displayName || admin.email,
                  email: admin.email,
                  avatarUrl: admin.avatarUrl,
                }
              : undefined
          }
          onSignOut={onSignOut}
          onContinueWithGoogle={
            admin
              ? undefined
              : () => {
                  window.location.assign(ADMIN_SIGNIN_BEGIN)
                }
          }
        />
        <div className="workspace">
          <Outlet />
        </div>
        {admin && (
          <CmdkPalette
            open={cmdkOpen}
            pages={PANELS}
            onClose={() => {
              setCmdkOpenState(false)
            }}
            onNavigate={href => {
              navigate(href)
            }}
            onAction={onCmdkAction}
          />
        )}
        {toast !== null && <div className="lc-admin-toast">{toast}</div>}
      </div>
    </ShellContext.Provider>
  )
}
